/**
 * Starter Modal — wraps Mantine's `<Modal>` with the design system's
 * elevation + scrim + radius defaults baked in.
 *
 * Composition (per design preview/comp-toasts.html-style elevation rules):
 *   - Scrim:    --bg-overlay (warm-black 45% light / pure-black 65% dark)
 *   - Surface:  --bg-surface + --radius-lg + --shadow-3
 *   - Title:    Libre Baskerville via theme.components.Title for h1/h2;
 *               header keeps the default body font here.
 *
 * All Mantine props (`size`, `centered`, `withCloseButton`, `overlayProps`,
 * `transitionProps`, ...) pass through unchanged.
 */
import { forwardRef } from 'react';
import {
  Modal as MantineModal,
  type ModalProps as MantineModalProps,
} from '@mantine/core';
import styles from './Modal.module.css';

export interface ModalProps extends MantineModalProps {}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  { classNames, overlayProps, ...rest },
  ref,
) {
  const existing = (classNames ?? {}) as {
    content?: string;
    overlay?: string;
    header?: string;
    title?: string;
  };
  const mergedClassNames = {
    ...existing,
    content: [styles.content, existing.content].filter(Boolean).join(' '),
    overlay: [styles.overlay, existing.overlay].filter(Boolean).join(' '),
    header: [styles.header, existing.header].filter(Boolean).join(' '),
    title: [styles.title, existing.title].filter(Boolean).join(' '),
  };

  return (
    <MantineModal
      ref={ref}
      classNames={mergedClassNames}
      {...(overlayProps !== undefined ? { overlayProps } : {})}
      {...rest}
    />
  );
});
